import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import { albumService, communityService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  TOTAL,
  countsFromBase64,
  getCachedUsers,
  buildTradeIndex,
  loadBulkCache,
  replaceBulkCache,
  stickerCode,
} from '../album/albumData';
import { useAlbumTrades } from '../album/hooks/useAlbumTrades';
import RankingCard from '../album/components/cards/RankingCard';
import UserSearchCard from '../album/components/cards/UserSearchCard';
import OffersBrowserSection from '../album/components/OffersBrowserSection';
import { buildWhatsappLink, buildMailtoLink, buildTradeMessage } from '../album/utils/whatsapp';

/* ── constants ──────────────────────────────────────────────────────── */

const TABS = ['Todos', 'Activas', 'Recibidas', 'Enviadas', 'Historial', 'Ofertas', 'Mis ofertas'];

const STATUS_CHIP = {
  ACCEPTED:  { label: 'Reservado', bg: 'bg-primary/10', text: 'text-primary' },
  PENDING:   { label: 'Pendiente', bg: 'bg-amber-100',  text: 'text-amber-800' },
  COMPLETED: { label: 'Completado', bg: 'bg-surface-container-highest', text: 'text-on-surface-variant' },
  CANCELLED: { label: 'Cancelado', bg: 'bg-red-50',    text: 'text-red-700' },
  REJECTED:  { label: 'Rechazado', bg: 'bg-red-50',    text: 'text-red-700' },
};

const SIDE_THEME = {
  cardSurface: 'var(--md-sys-color-surface)',
  cardRowBorder: 'var(--md-sys-color-outline-variant, rgba(0,0,0,.08))',
  cardRowHoverBg: 'var(--md-sys-color-surface-container)',
  cardHeadingText: 'var(--md-sys-color-on-surface-variant)',
  cardBodyText: 'var(--md-sys-color-on-surface)',
  cardSubtleText: 'var(--md-sys-color-outline)',
  primaryAccent: 'var(--md-sys-color-primary, #2563eb)',
  primaryAccentDim: '#1d4ed8',
  mutedText: 'var(--chrome-faint)',
};

/* ── helpers ────────────────────────────────────────────────────────── */

function timeAgo(dateValue) {
  if (!dateValue) return '';
  const d = Array.isArray(dateValue)
    ? new Date(dateValue[0], dateValue[1] - 1, dateValue[2], dateValue[3] || 0, dateValue[4] || 0, dateValue[5] || 0)
    : new Date(dateValue);
  if (!d || isNaN(d.getTime())) return '';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'justo ahora';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return `${Math.floor(diffD / 7)}sem`;
}

/* ── sub-components ─────────────────────────────────────────────────── */

function UserAvatar({ user, size = 9 }) {
  return (
    <div
      className={`w-${size} h-${size} rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0`}
      style={{ backgroundColor: user?.color || '#2563eb' }}
    >
      {user?.initial || '?'}
    </div>
  );
}

function StatusChip({ status }) {
  const cfg = STATUS_CHIP[status] || STATUS_CHIP.PENDING;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

function StickerChips({ nums, accent, label }) {
  if (!nums || nums.length === 0) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {label && (
        <span className="text-[10px] font-black uppercase tracking-widest mr-1" style={{ color: accent }}>
          {label}
        </span>
      )}
      {nums.slice(0, 4).map(n => (
        <span
          key={n}
          className="px-1.5 py-0.5 rounded text-[11px] font-bold tabular-nums"
          style={{ backgroundColor: `${accent}22`, color: accent }}
        >
          {stickerCode(n)}
        </span>
      ))}
      {nums.length > 4 && (
        <span className="text-[11px] font-bold text-on-surface-variant">+{nums.length - 4}</span>
      )}
    </div>
  );
}

function EmptyState({ icon, title, linkText, onLink }) {
  return (
    <div className="py-16 flex flex-col items-center justify-center text-center px-4">
      <span className="material-symbols-outlined text-5xl text-outline-variant mb-4">{icon}</span>
      <p className="text-sm font-bold text-on-surface mb-1">{title}</p>
      {linkText && onLink && (
        <a
          onClick={onLink}
          className="mt-2 text-sm font-bold text-primary cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1"
        >
          {linkText}
          <span className="material-symbols-outlined text-sm">arrow_forward</span>
        </a>
      )}
    </div>
  );
}

function LoadingRows({ count = 4 }) {
  return (
    <div className="divide-y divide-outline-variant/10">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="px-4 py-3 animate-pulse flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-surface-container-highest shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-surface-container-highest rounded w-2/5" />
            <div className="h-2 bg-surface-container-highest rounded w-1/3" />
          </div>
          <div className="h-6 bg-surface-container-highest rounded-full w-20" />
        </div>
      ))}
    </div>
  );
}

function SectionHeader({ icon, label, count }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-surface-container border-b border-outline-variant/20">
      <span className="material-symbols-outlined text-sm text-on-surface-variant">{icon}</span>
      <span className="text-[11px] font-black uppercase tracking-widest text-on-surface-variant">{label}</span>
      <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary tabular-nums">
        {count}
      </span>
    </div>
  );
}

/* ── main component ─────────────────────────────────────────────────── */

function AlbumDashboard({ embedded = false, onClose } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = getThemeVars(ACTIVE_THEME);

  /* ── album data bootstrap ── */
  const [bulkCache, setBulkCache] = useState(() => loadBulkCache());
  const [myBase64, setMyBase64] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await albumService.getMyAlbum();
        if (!cancelled) setMyBase64(res.data?.data ?? null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Primer sync por sesión: reemplazamos el cache (en vez de mergear) para
  // purgar usuarios con los que ya no compartimos comunidad.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await albumService.getBulkAlbums();
        const bulk = res.data;
        if (bulk && !cancelled) {
          const updated = replaceBulkCache(bulk.serverTime, bulk.albums || []);
          setBulkCache({ ...updated });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── current communities (defensive client-side filter for stale cache) ── */
  const [myCommunities, setMyCommunities] = useState([]);
  useEffect(() => {
    let cancelled = false;
    communityService.getUserCommunities()
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : []);
        setMyCommunities(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const myCommunityIdSet = useMemo(() => {
    if (!Array.isArray(myCommunities) || myCommunities.length === 0) return null;
    return new Set(myCommunities.map(c => c.id));
  }, [myCommunities]);

  const myCounts = useMemo(() => myBase64 ? countsFromBase64(myBase64) : new Uint8Array(TOTAL), [myBase64]);
  const allUsers = useMemo(
    () => getCachedUsers(bulkCache, myCommunityIdSet).filter(u => u.id !== user?.id),
    [bulkCache, myCommunityIdSet, user?.id]
  );
  const tradeIndex = useMemo(() => buildTradeIndex(bulkCache), [bulkCache]);

  /* ── trades hook ── */
  const tradesHook = useAlbumTrades({ bulkCache });

  /* ── my offers (Mis ofertas tab) ── */
  const [myOffers, setMyOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  const loadMyOffers = useCallback(async () => {
    setOffersLoading(true);
    try {
      const res = await albumService.listOffers('mine');
      const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setMyOffers(raw.filter(o => o.status !== 'CANCELLED'));
    } catch { /* silent */ } finally {
      setOffersLoading(false);
    }
  }, []);

  /* ── tab state ── */
  const urlTab = useMemo(() => {
    try {
      const tabParam = new URLSearchParams(location.search).get('tab');
      if (tabParam && TABS.includes(tabParam)) return tabParam;
    } catch {}
    return TABS[0];
  }, [location.search]);

  const [activeTab, setActiveTab] = useState(urlTab);

  useEffect(() => {
    if (embedded) return;
    setActiveTab(current => (current === urlTab ? current : urlTab));
  }, [embedded, urlTab]);

  const selectTab = useCallback((tab) => {
    setActiveTab(tab);
    if (embedded) return;
    const nextPath = tab === TABS[0]
      ? '/album/dashboard'
      : `/album/dashboard?tab=${encodeURIComponent(tab)}`;
    navigate(nextPath, { replace: true });
  }, [embedded, navigate]);

  useEffect(() => {
    if (activeTab === 'Mis ofertas' || activeTab === 'Todos') loadMyOffers();
  }, [activeTab, loadMyOffers]);

  /* ── toast state ── */
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  /* ── opening trade/offer detail navigates to /album with context ── */
  const openTrade = useCallback((trade) => {
    navigate(`/album?tradeId=${trade.id}`);
  }, [navigate]);
  const openOffer = useCallback((offerId) => {
    navigate(`/album?offerId=${offerId}`);
  }, [navigate]);

  /* ── post-complete prompt: detect trades the counterparty marked as completed ── */
  // Estado persistente: cada vez que el usuario hace "Listo" o ve el detalle,
  // el id queda marcado como acknowledged en localStorage. Así el banner no
  // vuelve a saltar al cambiar de página o después de un refresh.
  const ACK_KEY = 'cm_album_complete_acked';
  const loadAcked = () => {
    try {
      const raw = localStorage.getItem(ACK_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  };
  const saveAcked = (set) => {
    try { localStorage.setItem(ACK_KEY, JSON.stringify([...set])); } catch {}
  };
  const myCompletesRef = useRef(new Set());
  const seenHistoryIdsRef = useRef(new Set());
  const seededHistoryRef = useRef(false);
  const ackedRef = useRef(loadAcked());
  const [completePrompt, setCompletePrompt] = useState(null);

  useEffect(() => {
    const list = tradesHook.historyTrades;
    const ids = new Set(list.map(tr => tr.id));

    // Seed solo cuando hay data real. Mientras la lista esté vacía (todavía no
    // cargó / hay 0 históricos) ignoramos el efecto: si no, después llega un
    // batch con N entries y todas se ven "nuevas".
    if (!seededHistoryRef.current) {
      if (list.length === 0) return;
      seenHistoryIdsRef.current = ids;
      seededHistoryRef.current = true;
      return;
    }
    for (const trade of list) {
      if (seenHistoryIdsRef.current.has(trade.id)) continue;
      if (myCompletesRef.current.has(trade.id)) continue;
      if (ackedRef.current.has(trade.id)) continue;
      setCompletePrompt(trade);
      break;
    }
    seenHistoryIdsRef.current = ids;
  }, [tradesHook.historyTrades]);

  const acknowledgePrompt = useCallback((tradeId) => {
    if (!tradeId) return;
    ackedRef.current.add(tradeId);
    saveAcked(ackedRef.current);
  }, []);

  /* ── action handlers ── */
  const handleAccept = useCallback(async (trade, e) => {
    e?.stopPropagation();
    try {
      await albumService.acceptTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Aceptar') + ' — OK');
    } catch {
      showToast(t('Error al aceptar la reserva'), 'error');
    }
  }, [tradesHook, showToast, t]);

  const handleReject = useCallback(async (trade, e) => {
    e?.stopPropagation();
    try {
      await albumService.rejectTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Rechazar') + ' — OK');
    } catch {
      showToast(t('Error al rechazar la reserva'), 'error');
    }
  }, [tradesHook, showToast, t]);

  const handleCancel = useCallback(async (trade, e) => {
    e?.stopPropagation();
    try {
      await albumService.cancelTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Reserva cancelada'));
    } catch {
      showToast(t('No se pudo cancelar la reserva'), 'error');
    }
  }, [tradesHook, showToast, t]);

  const handleComplete = useCallback(async (trade, e) => {
    e?.stopPropagation();
    if (!window.confirm(t('¿Ya intercambiaron las figuritas en persona? Esto las moverá entre los álbumes y no se puede deshacer.'))) return;
    try {
      myCompletesRef.current.add(trade.id);
      await albumService.completeTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Intercambio completado'));
    } catch {
      showToast(t('No se pudo completar el intercambio'), 'error');
    }
  }, [tradesHook, showToast, t]);

  const handleWhatsapp = useCallback(async (trade, e) => {
    e?.stopPropagation();
    const contact = await tradesHook.fetchContact(trade.id);
    const phone = contact?.phone;
    const message = buildTradeMessage(t, {
      theirName: trade.otherUser?.name,
      myName: user?.name,
      giveNums: trade.youGive,
      getNums: trade.youReceive,
    });
    const link = phone ? buildWhatsappLink(phone, message) : null;
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
      return;
    }
    showToast(t('El otro usuario no tiene número de WhatsApp asociado'), 'error');
  }, [tradesHook, showToast, t, user?.name]);

  const handleCancelOffer = useCallback(async (offerId, e) => {
    e?.stopPropagation();
    if (!window.confirm(t('¿Cancelar esta oferta? Los reclamos pendientes se rechazarán.'))) return;
    try {
      await albumService.cancelOffer(offerId);
      await loadMyOffers();
      showToast(t('Cancelar oferta') + ' — OK');
    } catch {
      showToast(t('No se pudo cancelar la oferta'), 'error');
    }
  }, [loadMyOffers, showToast, t]);

  /* ── metrics ── */
  const metrics = useMemo(() => ({
    activas: tradesHook.activeTrades.length,
    recibidas: tradesHook.incomingTrades.length,
    enviadas: tradesHook.outgoingTrades.length,
    completadas: tradesHook.historyTrades.length,
  }), [tradesHook.activeTrades, tradesHook.incomingTrades, tradesHook.outgoingTrades, tradesHook.historyTrades]);

  /* ── sidebar shared props for right panel ── */
  const handleViewUser = useCallback((target) => {
    navigate(`/album?userId=${target.id}`);
  }, [navigate]);

  const sharedProps = {
    dispatch: () => {},
    myCounts,
    allUsers,
    tradeIndex,
    incomingTrades: tradesHook.incomingTrades,
    outgoingTrades: tradesHook.outgoingTrades,
    activeTrades: tradesHook.activeTrades,
    onViewUser: handleViewUser,
    onToast: showToast,
  };

  /* ── row renderers ── */

  /* TradeRow: tira teñida por estado (B) + chips en popover flotante (C). */
  function TradeRow({ trade, actions }) {
    const giveCount = trade.youGive?.length || 0;
    const receiveCount = trade.youReceive?.length || 0;
    const onClickRow = () => openTrade(trade);

    const tint =
      trade.status === 'ACCEPTED' ? { bg: 'bg-primary/8',  border: 'border-l-primary',     hover: 'hover:bg-primary/12' } :
      trade.direction === 'receiver' && trade.status === 'PENDING'
        ? { bg: 'bg-amber-50',     border: 'border-l-amber-500',  hover: 'hover:bg-amber-100' } :
      { bg: 'bg-surface-container/50', border: 'border-l-outline-variant', hover: 'hover:bg-surface-container' };

    return (
      <div
        className={`relative flex items-center gap-4 pl-3 pr-4 py-4 ${tint.bg} ${tint.hover} border-l-4 ${tint.border} transition-colors group cursor-pointer border-b border-outline-variant/10 last:border-b-0`}
        onClick={onClickRow}
        title={t('Click para ver en el álbum')}
      >
        <UserAvatar user={trade.otherUser} size={11} />
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-on-surface truncate">{trade.otherUser?.name}</p>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-bold" style={{ color: '#b91c1c' }}>
              {t('Das')} <span className="tabular-nums">{giveCount}</span>
            </span>
            <span className="font-bold" style={{ color: '#15803d' }}>
              {t('Recibís')} <span className="tabular-nums">{receiveCount}</span>
            </span>
            <span className="text-xs text-on-surface-variant">· {timeAgo(trade.createdAt)}</span>
          </div>
        </div>
        <StatusChip status={trade.status} />
        <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
          {actions(trade)}
        </div>
        {(giveCount > 0 || receiveCount > 0) && (
          <div className="hidden group-hover:block absolute left-4 right-4 top-full mt-1 z-30 bg-surface border border-outline rounded-xl shadow-2xl p-3 pointer-events-none">
            <div className="space-y-2">
              {giveCount > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-xs font-black uppercase tracking-widest w-16 shrink-0 mt-0.5" style={{ color: '#b91c1c' }}>{t('Das')}</span>
                  <div className="flex flex-wrap gap-1">
                    {trade.youGive.map(n => (
                      <span key={n} className="px-1.5 py-0.5 rounded text-xs font-bold tabular-nums" style={{ backgroundColor: '#ef444422', color: '#b91c1c' }}>{stickerCode(n)}</span>
                    ))}
                  </div>
                </div>
              )}
              {receiveCount > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-xs font-black uppercase tracking-widest w-16 shrink-0 mt-0.5" style={{ color: '#15803d' }}>{t('Recibís')}</span>
                  <div className="flex flex-wrap gap-1">
                    {trade.youReceive.map(n => (
                      <span key={n} className="px-1.5 py-0.5 rounded text-xs font-bold tabular-nums" style={{ backgroundColor: '#16a34a22', color: '#15803d' }}>{stickerCode(n)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  function HistoryRow({ trade }) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 hover:bg-surface-container transition-colors group cursor-pointer border-b border-outline-variant/10 last:border-b-0"
        onClick={() => openTrade(trade)}
      >
        <UserAvatar user={trade.otherUser} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-on-surface truncate">{trade.otherUser?.name}</p>
          <p className="text-[10px] text-on-surface-variant">{timeAgo(trade.completedAt || trade.resolvedAt || trade.createdAt)}</p>
        </div>
        <div className="hidden tablet-sm:flex flex-col gap-1">
          <span className="text-[10px] text-on-surface-variant">{t('Diste {{n}}', { n: trade.youGive?.length || 0 })}</span>
          <span className="text-[10px] text-on-surface-variant">{t('Recibiste {{n}}', { n: trade.youReceive?.length || 0 })}</span>
        </div>
        <StatusChip status={trade.status} />
      </div>
    );
  }

  function OfferRow({ offer }) {
    const isOpen = offer.status === 'OPEN';
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 hover:bg-surface-container transition-colors group cursor-pointer border-b border-outline-variant/10 last:border-b-0"
        onClick={() => openOffer(offer.id)}
      >
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-base text-primary">swap_horiz</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-on-surface truncate">
            {t('da {{n}}', { n: offer.offered?.length || 0 })} · {t('pide {{n}}', { n: offer.requested?.length || 0 })}
          </p>
          <p className="text-[10px] text-on-surface-variant">#{offer.id}</p>
        </div>
        <div className="hidden tablet-sm:flex items-center gap-2 flex-wrap">
          <StickerChips nums={offer.offered} accent="#ef4444" label={t('Das')} />
          <StickerChips nums={offer.requested} accent="#16a34a" label={t('Pedís')} />
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${isOpen ? 'bg-primary/10 text-primary' : 'bg-surface-container-highest text-on-surface-variant'}`}>
          {isOpen ? 'Abierta' : t('Cerrada')}
        </span>
        {isOpen && (
          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={(e) => handleCancelOffer(offer.id, e)}
              className="px-2 py-1 text-[11px] font-bold rounded-lg border border-red-300 text-red-600 bg-transparent hover:bg-red-50 transition-colors"
            >
              <span className="hidden tablet-sm:inline">{t('Cancelar oferta')}</span>
              <span className="material-symbols-outlined text-sm tablet-sm:hidden">close</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ── tab body ── */

  // Extracted action renderers — reused by individual tabs and the "Todos" tab
  const renderActiveActions = (trade) => (
    <>
      <button
        type="button"
        onClick={(e) => handleWhatsapp(trade, e)}
        className="p-1.5 rounded-lg border border-outline bg-surface hover:bg-surface-container transition-colors"
        title={t('Abrir WhatsApp')}
      >
        <span className="material-symbols-outlined text-sm" style={{ color: '#25D366' }}>chat</span>
      </button>
      <button
        type="button"
        onClick={(e) => handleCancel(trade, e)}
        className="hidden tablet-sm:flex px-2 py-1 text-[11px] font-bold rounded-lg border border-red-300 text-red-600 bg-transparent hover:bg-red-50 transition-colors"
      >
        {t('Cancelar reserva')}
      </button>
      <button
        type="button"
        onClick={(e) => handleComplete(trade, e)}
        className="px-2 py-1 text-[11px] font-bold rounded-lg text-white hover:opacity-90 transition-opacity"
        style={{ backgroundColor: 'var(--md-sys-color-primary, #2563eb)' }}
      >
        <span className="hidden tablet-sm:inline">{t('Completar')}</span>
        <span className="material-symbols-outlined text-sm tablet-sm:hidden">check_circle</span>
      </button>
    </>
  );

  const renderIncomingActions = (trade) => (
    <>
      <button
        type="button"
        onClick={(e) => handleAccept(trade, e)}
        className="px-2 py-1 text-[11px] font-bold rounded-lg text-white hover:opacity-90 transition-opacity"
        style={{ backgroundColor: 'var(--md-sys-color-primary, #2563eb)' }}
      >
        <span className="hidden tablet-sm:inline">{t('Aceptar')}</span>
        <span className="material-symbols-outlined text-sm tablet-sm:hidden">check</span>
      </button>
      <button
        type="button"
        onClick={(e) => handleReject(trade, e)}
        className="hidden tablet-sm:flex px-2 py-1 text-[11px] font-bold rounded-lg border border-red-300 text-red-600 bg-transparent hover:bg-red-50 transition-colors"
      >
        {t('Rechazar')}
      </button>
    </>
  );

  const renderOutgoingActions = (trade) => (
    <button
      type="button"
      onClick={(e) => handleCancel(trade, e)}
      className="px-2 py-1 text-[11px] font-bold rounded-lg border border-red-300 text-red-600 bg-transparent hover:bg-red-50 transition-colors"
    >
      <span className="hidden tablet-sm:inline">{t('Cancelar')}</span>
      <span className="material-symbols-outlined text-sm tablet-sm:hidden">close</span>
    </button>
  );

  function TabBody() {
    const isTodos = activeTab === 'Todos';

    if (tradesHook.loading && activeTab !== 'Mis ofertas' && activeTab !== 'Ofertas') {
      return <LoadingRows />;
    }
    if (offersLoading && (activeTab === 'Mis ofertas' || (isTodos && myOffers.length === 0))) {
      return <LoadingRows />;
    }

    if (isTodos) {
      const sections = [
        { key: 'Activas',   icon: 'swap_horiz',  label: t('Activas'),   list: tradesHook.activeTrades,   render: (tr) => <TradeRow key={tr.id} trade={tr} actions={renderActiveActions} /> },
        { key: 'Recibidas', icon: 'inbox',       label: t('Recibidas'), list: tradesHook.incomingTrades, render: (tr) => <TradeRow key={tr.id} trade={tr} actions={renderIncomingActions} /> },
        { key: 'Enviadas',  icon: 'outbox',      label: t('Enviadas'),  list: tradesHook.outgoingTrades, render: (tr) => <TradeRow key={tr.id} trade={tr} actions={renderOutgoingActions} /> },
        { key: 'Historial', icon: 'history',     label: t('Historial'), list: tradesHook.historyTrades,  render: (tr) => <HistoryRow key={tr.id} trade={tr} /> },
        { key: 'Ofertas',   icon: 'local_offer', label: t('Mis ofertas'), list: myOffers,               render: (of) => <OfferRow key={of.id} offer={of} /> },
      ];
      const hasAny = sections.some(s => s.list.length > 0);
      if (!hasAny) {
        return (
          <EmptyState
            icon="swap_horiz"
            title={t('Todavía no tenés actividad en el álbum.')}
            linkText={t('Ir al álbum')}
            onLink={() => navigate('/album')}
          />
        );
      }
      return (
        <div>
          {sections.filter(s => s.list.length > 0).map(s => (
            <React.Fragment key={s.key}>
              <SectionHeader icon={s.icon} label={s.label} count={s.list.length} />
              {s.list.map(s.render)}
            </React.Fragment>
          ))}
        </div>
      );
    }

    if (activeTab === 'Activas') {
      const list = tradesHook.activeTrades;
      if (list.length === 0) {
        return (
          <EmptyState
            icon="swap_horiz"
            title={t('No tenés intercambios activos.')}
            linkText={t('Ir al álbum')}
            onLink={() => navigate('/album')}
          />
        );
      }
      return (
        <div>
          {list.map(trade => (
            <TradeRow key={trade.id} trade={trade} actions={renderActiveActions} />
          ))}
        </div>
      );
    }

    if (activeTab === 'Recibidas') {
      const list = tradesHook.incomingTrades;
      if (list.length === 0) {
        return (
          <EmptyState
            icon="inbox"
            title={t('No tenés propuestas recibidas.')}
            linkText={t('Ir al álbum')}
            onLink={() => navigate('/album')}
          />
        );
      }
      return (
        <div>
          {list.map(trade => (
            <TradeRow key={trade.id} trade={trade} actions={renderIncomingActions} />
          ))}
        </div>
      );
    }

    if (activeTab === 'Enviadas') {
      const list = tradesHook.outgoingTrades;
      if (list.length === 0) {
        return (
          <EmptyState
            icon="outbox"
            title={t('No tenés propuestas enviadas.')}
            linkText={t('Ir al álbum')}
            onLink={() => navigate('/album')}
          />
        );
      }
      return (
        <div>
          {list.map(trade => (
            <TradeRow key={trade.id} trade={trade} actions={renderOutgoingActions} />
          ))}
        </div>
      );
    }

    if (activeTab === 'Historial') {
      const list = tradesHook.historyTrades;
      if (list.length === 0) {
        return (
          <EmptyState
            icon="history"
            title={t('Aún no hiciste intercambios.')}
            linkText={t('Ir al álbum')}
            onLink={() => navigate('/album')}
          />
        );
      }
      return (
        <div>
          {list.map(trade => (
            <HistoryRow key={trade.id} trade={trade} />
          ))}
        </div>
      );
    }

    if (activeTab === 'Mis ofertas') {
      if (myOffers.length === 0) {
        return (
          <EmptyState
            icon="local_offer"
            title={t('No publicaste ofertas todavía')}
            linkText={t('Crear oferta')}
            onLink={() => navigate('/album')}
          />
        );
      }
      return (
        <div>
          {myOffers.map(offer => (
            <OfferRow key={offer.id} offer={offer} />
          ))}
        </div>
      );
    }

    if (activeTab === 'Ofertas') {
      return (
        <OffersBrowserSection
          embedded
          communities={myCommunities}
          currentUserId={user?.id}
          onOpenOffer={(offer) => openOffer(offer.id)}
        />
      );
    }

    return null;
  }

  /* ── render ── */

  return (
    <div
      className={
        embedded
          ? 'dashboard-new font-body h-full flex flex-col'
          : 'dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row'
      }
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      {!embedded && <DashboardSidebar activePath="/album/dashboard" />}

      <main className={embedded ? 'flex-1 overflow-y-auto hide-scrollbar' : 'flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav'}>
        <div className={embedded ? 'px-4 py-3' : 'px-3 mobile-md:px-6 tablet-sm:px-12 pt-3 mobile-md:pt-8 pb-20 max-w-6xl mx-auto'}>

          {/* ── Toast ── */}
          {toast && (
            <div
              className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl shadow-lg text-sm font-bold flex items-center gap-2 transition-all ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-surface-container text-on-surface border border-outline'}`}
            >
              <span className="material-symbols-outlined text-sm">
                {toast.type === 'error' ? 'error' : 'check_circle'}
              </span>
              {toast.message}
            </div>
          )}

          {/* ── Post-complete prompt: counterparty marked the trade as done ── */}
          {completePrompt && (
            <div
              className="mb-6 p-4 rounded-xl border bg-primary/5 flex flex-col mobile-sm:flex-row mobile-sm:items-center gap-3"
              style={{ borderColor: 'var(--md-sys-color-primary, #2563eb)' }}
            >
              <span className="material-symbols-outlined text-2xl text-primary shrink-0">celebration</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-on-surface">
                  {t('{{name}} completó el intercambio', { name: completePrompt.otherUser?.name || '' })}
                </p>
                <p className="text-xs text-on-surface-variant mt-0.5">
                  {t('Las figuritas ya están en tu álbum. Confirmá si recibiste todo en persona.')}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => { acknowledgePrompt(completePrompt.id); openTrade(completePrompt); setCompletePrompt(null); }}
                  className="px-3 py-1.5 text-xs font-bold rounded-lg border border-outline text-on-surface bg-surface hover:bg-surface-container transition-colors"
                >
                  {t('Ver detalle')}
                </button>
                <button
                  type="button"
                  onClick={() => { acknowledgePrompt(completePrompt.id); setCompletePrompt(null); }}
                  className="px-3 py-1.5 text-xs font-bold rounded-lg text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: 'var(--md-sys-color-primary, #2563eb)' }}
                >
                  {t('Listo')}
                </button>
              </div>
            </div>
          )}

          {/* ── Header ── */}
          {embedded ? (
            <div className="grid grid-cols-4 gap-2 mb-3">
              <MiniMetric bucket="activas"     label={t('Activas')}     value={metrics.activas}     trades={tradesHook.activeTrades}    onPick={openTrade} />
              <MiniMetric bucket="recibidas"   label={t('Recibidas')}   value={metrics.recibidas}   trades={tradesHook.incomingTrades}  onPick={openTrade} />
              <MiniMetric bucket="enviadas"    label={t('Enviadas')}    value={metrics.enviadas}    trades={tradesHook.outgoingTrades}  onPick={openTrade} />
              <MiniMetric bucket="completadas" label={t('Completadas')} value={metrics.completadas} trades={tradesHook.historyTrades}   onPick={openTrade} />
            </div>
          ) : (
            <>
              {/* Header compact: en mobile arrow + título chico inline; en
                  tablet+ vuelve al header grande con subtitle. */}
              <div className="flex items-center gap-2 mobile-md:gap-4 mb-3 mobile-md:mb-6">
                <a
                  onClick={() => navigate('/album')}
                  className="p-1.5 mobile-md:p-2 rounded-xl hover:bg-surface-container-highest cursor-pointer transition-colors shrink-0"
                  title={t('Volver al Álbum')}
                  aria-label={t('Volver al Álbum')}
                >
                  <span className="material-symbols-outlined text-on-surface-variant text-[20px] mobile-md:text-[24px]">arrow_back</span>
                </a>
                <div className="min-w-0 flex-1">
                  <h1 className="text-base mobile-md:text-3xl font-black text-on-surface tracking-tight leading-tight truncate">
                    {t('Mis intercambios')}
                  </h1>
                  <p className="hidden mobile-md:block text-on-surface-variant text-sm font-medium truncate">
                    {t('Tus intercambios y coincidencias en un solo lugar')}
                  </p>
                </div>
              </div>

              {/* Metrics: 2x2 en mobile, fila inline en tablet+. Pads
                  reducidos en mobile para no comer espacio. */}
              <div className="bg-surface-container px-3 py-2 mobile-md:px-5 mobile-md:py-3 rounded-xl grid grid-cols-4 gap-1.5 mobile-md:grid-cols-none mobile-md:flex mobile-md:items-center mobile-md:gap-4 mb-3 mobile-md:mb-6">
                {[
                  { key: 'activas',     label: t('Activas'),     value: metrics.activas,     color: 'text-primary' },
                  { key: 'recibidas',   label: t('Recibidas'),   value: metrics.recibidas,   color: 'text-secondary' },
                  { key: 'enviadas',    label: t('Enviadas'),    value: metrics.enviadas,    color: 'text-on-surface-variant' },
                  { key: 'completadas', label: t('Completadas'), value: metrics.completadas, color: 'text-on-surface-variant' },
                ].map((m, i) => (
                  <React.Fragment key={m.key}>
                    <div className="flex flex-col items-center mobile-md:items-start min-w-0">
                      <span className="text-[8px] mobile-md:text-[10px] uppercase tracking-wider mobile-md:tracking-widest text-on-surface-variant font-bold truncate">
                        {m.label}
                      </span>
                      <span className={`text-base mobile-md:text-xl font-black tabular-nums ${m.color}`}>
                        {m.value}
                      </span>
                    </div>
                    {i < 3 && <div className="hidden mobile-md:block w-[1px] h-8 bg-outline-variant/20" />}
                  </React.Fragment>
                ))}
              </div>
            </>
          )}

          {/* ── Body: list + optional right panel ── */}
          <div className={embedded ? '' : 'flex gap-6'}>

            {/* ── Main column ── */}
            <div className="flex-1 min-w-0">

              {/* Filter bar — en mobile usamos un <select> nativo para que
                  los 7 tabs no compitan por espacio; en tablet+ vuelven los
                  pills de la versión desktop. */}
              {!embedded && (
                <div className="mobile-md:hidden mb-3">
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mb-1">
                    {t('Filtro')}
                  </label>
                  <div className="relative">
                    <select
                      value={activeTab}
                      onChange={(e) => selectTab(e.target.value)}
                      className="w-full appearance-none px-3 py-2.5 pr-9 rounded-xl border border-outline bg-surface text-sm font-bold text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      {TABS.map((tab) => (
                        <option key={tab} value={tab}>{t(tab)}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined text-[20px] absolute right-2 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none">expand_more</span>
                  </div>
                </div>
              )}

              <div className={embedded
                ? 'mb-3 bg-surface-container rounded-xl p-1 overflow-x-auto hide-scrollbar'
                : 'hidden mobile-md:flex mb-6 w-full p-1 bg-surface-container border border-outline rounded-xl overflow-x-auto hide-scrollbar'}>
                {embedded ? (
                  <div className="flex w-full">
                    {TABS.map(tab => (
                      <a
                        key={tab}
                        onClick={() => selectTab(tab)}
                        className={`flex-1 px-2 py-1 text-[11px] rounded-lg text-center font-bold cursor-pointer transition-colors whitespace-nowrap ${
                          activeTab === tab
                            ? 'bg-surface-container-lowest text-primary shadow-sm'
                            : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                      >
                        {t(tab)}
                      </a>
                    ))}
                  </div>
                ) : (
                  TABS.map(tab => (
                    <a
                      key={tab}
                      onClick={() => selectTab(tab)}
                      className={`flex-1 px-4 py-1.5 text-sm rounded-lg text-center font-bold cursor-pointer transition-colors whitespace-nowrap ${
                        activeTab === tab
                          ? 'bg-surface-container-lowest text-primary shadow-sm'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      {t(tab)}
                    </a>
                  ))
                )}
              </div>

              {/* ── List container ── */}
              <div className={embedded
                ? 'bg-surface rounded-xl overflow-hidden shadow-md ring-1 ring-outline-variant'
                : 'bg-surface border border-outline rounded-xl overflow-hidden'}>
                <TabBody />
              </div>
            </div>



          </div>
        </div>
      </main>
    </div>
  );
}

/* MiniMetric: chip teñido por bucket; al hover muestra popover flotante con la
   lista de trades de ese bucket. Click en una fila navega al trade en el álbum. */
function MiniMetric({ bucket, label, value, trades, onPick }) {
  const palette = {
    activas:     { num: 'text-primary',     bg: 'bg-primary/10',         border: 'border-primary/40',     dot: 'bg-primary' },
    recibidas:   { num: 'text-amber-700',   bg: 'bg-amber-100',          border: 'border-amber-400',      dot: 'bg-amber-500' },
    enviadas:    { num: 'text-on-surface',  bg: 'bg-surface-container',  border: 'border-outline-variant',dot: 'bg-on-surface-variant' },
    completadas: { num: 'text-on-surface-variant', bg: 'bg-surface-container-highest', border: 'border-outline-variant', dot: 'bg-outline-variant' },
  }[bucket] || { num: 'text-on-surface', bg: 'bg-surface-container', border: 'border-outline-variant', dot: 'bg-on-surface-variant' };

  const hasTrades = trades && trades.length > 0;

  return (
    <div className={`relative group ${hasTrades ? 'cursor-default' : ''}`}>
      <div className={`flex flex-col items-center justify-center px-2 py-2 rounded-xl border ${palette.bg} ${palette.border}`}>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`} aria-hidden="true" />
          <span className={`text-lg font-black tabular-nums ${palette.num}`}>{value}</span>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mt-0.5">{label}</span>
      </div>
      {hasTrades && (
        <div className="hidden group-hover:block absolute left-0 right-0 top-full mt-1 z-30 bg-surface border border-outline rounded-xl shadow-2xl p-2 max-h-72 overflow-y-auto pointer-events-auto">
          {trades.slice(0, 6).map(tr => (
            <button
              type="button"
              key={tr.id}
              onClick={() => onPick?.(tr)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-container text-left"
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ backgroundColor: tr.otherUser?.color || '#6b7280' }}>
                {tr.otherUser?.initial || '?'}
              </div>
              <span className="flex-1 text-xs font-bold text-on-surface truncate">{tr.otherUser?.name}</span>
              <span className="text-[10px] text-on-surface-variant tabular-nums">
                {(tr.youGive?.length || 0)}↧ {(tr.youReceive?.length || 0)}↥
              </span>
            </button>
          ))}
          {trades.length > 6 && (
            <p className="px-2 py-1 text-[10px] text-on-surface-variant text-center">+{trades.length - 6} {label.toLowerCase()}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default AlbumDashboard;
